import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import TutorLayout from "./TutorLayout";
import formatLastSeen from "./formatLastSeen.js";

function ViewUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      const snapshot = await getDocs(collection(db, "users"));
      const usersData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    };

    fetchUsers();
  }, []);

  // Inject glow animation
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes glowPulse {
        0% { text-shadow: 0 0 2px rgba(0, 255, 0, 0.3); }
        50% { text-shadow: 0 0 10px rgba(0, 255, 0, 0.8); }
        100% { text-shadow: 0 0 2px rgba(0, 255, 0, 0.3); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const statusDisplay = {
    online: { text: "ONLINE", color: "green", emoji: "🟢" },
    idle: { text: "IDLE", color: "goldenrod", emoji: "🟡" },
    offline: { text: "OFFLINE", color: "gray", emoji: "🔴" },
    unknown: { text: "NOT YET LOGGED IN", color: "red", emoji: "❌" },
  };

  const filteredUsers = users.filter((user) => {
    const fullName = `${user.displayName || ""} ${user.surname || ""}`.toLowerCase();
    return (
      fullName.includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase())
    );
  });

  const stats = {
    online: users.filter((u) => u.status === "online").length,
    idle: users.filter((u) => u.status === "idle").length,
    offline: users.filter((u) => u.status === "offline").length,
    total: users.length,
  };

  return (
    <TutorLayout>
      <div style={styles.page}>
        <h1 style={{ marginBottom: "1rem" }}>👥 All Users</h1>

        {/* Stats Bar */}
        <div style={styles.statsBar}>
          <div>🟢 Online: {stats.online}</div>
          <div>🟡 Idle: {stats.idle}</div>
          <div>🔴 Offline: {stats.offline}</div>
          <div>👤 Total: {stats.total}</div>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.search}
        />

        {/* Users Grid */}
        <div style={styles.grid}>
          {filteredUsers.map((user) => {
            const fullName = `${user.displayName || "Unnamed"} ${user.surname || ""}`.trim();
            const initials = fullName
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase();
            const role = user.role || "unknown";
            const statusKey = user.status || "unknown";
            const status = statusDisplay[statusKey] || statusDisplay.unknown;
            const lastLogin = user.lastLogin?.toDate?.();
            const formattedLastLogin = lastLogin
              ? new Intl.DateTimeFormat("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                }).format(lastLogin)
              : "N/A";

            return (
              <div key={user.id} style={styles.card}>
                {/* Avatar */}
                <div style={styles.avatar}>{initials}</div>

                {/* Name */}
                <h2 style={{ marginBottom: "0.25rem" }}>{fullName}</h2>

                {/* Email */}
                <div style={styles.detail}>📧 {user.email}</div>

                {/* Role */}
                <div style={styles.detail}>
                  {role === "tutor" ? "🎓 Tutor" : "👩‍🎓 Student"}
                </div>

                {/* Status */}
                <div style={styles.detail}>
                  {status.emoji}{" "}
                  <span
                    style={
                      statusKey === "online"
                        ? styles.glow
                        : { color: status.color }
                    }
                  >
                    {status.text}
                  </span>
                </div>

                {/* Last Seen */}
                {statusKey !== "online" && (
                  <div style={styles.detail}>🕒 {formatLastSeen(user.lastActive)}</div>
                )}

                {/* Last Login */}
                <div style={styles.detail}>⏰ Last Login: {formattedLastLogin}</div>
              </div>
            );
          })}
        </div>
      </div>
    </TutorLayout>
  );
}

const styles = {
  page: {
    padding: "2rem",
    fontFamily: "'Poppins', sans-serif",
  },
  statsBar: {
    display: "flex",
    gap: "2rem",
    marginBottom: "1.2rem",
    fontSize: "1rem",
    fontWeight: "500",
  },
  search: {
    marginBottom: "1.5rem",
    padding: "0.6rem 1rem",
    fontSize: "0.95rem",
    width: "100%",
    maxWidth: "320px",
    borderRadius: "8px",
    border: "1px solid #ccc",
  },
  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "1.5rem",
  },
  card: {
    backgroundColor: "#fff3da",
    borderRadius: "12px",
    padding: "1.25rem",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    width: "320px",
    wordWrap: "break-word",
  },
  avatar: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: "#ccc",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "1.2rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "0.5rem",
  },
  detail: {
    marginTop: "0.4rem",
    fontSize: "0.9rem",
  },
  glow: {
    animation: "glowPulse 1.8s ease-in-out infinite",
    color: "green",
    fontWeight: "regular",
  },
};

export default ViewUsers;
